import { Trans } from '@lingui/react/macro';

import type { AudienceType } from '@/api/audience';
import {
  type AudienceMinSizeIntegrationSetting,
  type AudienceTypeIntegrationSetting,
  INTEGRATION_PROVIDER_ID,
  type IntegrationProviderType,
} from '@/api/integration';
import {
  getAudienceCapability,
  getAudienceSettingFromCapability,
} from '@/app/audiences/audience-capability-util';

type ActivationRequirements = {
  audienceType?: string;
  minSizeString?: string;
  minSize?: number;
  memberCount?: number;
};

export const getAudienceMemberCount = (audience: AudienceType, memberType: string | undefined) => {
  switch (memberType) {
    case 'email':
      return audience.metrics.emails;
    case 'ip':
      return audience.metrics.ips;
    case 'domain':
      return audience.metrics.members;
    default:
      return undefined;
  }
};

export const getActivationRequirements = (
  audience: AudienceType,
  provider: IntegrationProviderType,
): ActivationRequirements => {
  const activationCapability = getAudienceCapability({
    provider,
    capabilityId: 'audienceActivation',
  });

  const audienceTypeSetting = getAudienceSettingFromCapability({
    capability: activationCapability,
    settingName: 'audienceType',
  }) as AudienceTypeIntegrationSetting | undefined;

  const minSizeSetting = getAudienceSettingFromCapability({
    capability: activationCapability,
    settingName: 'minSize',
  }) as AudienceMinSizeIntegrationSetting | undefined;

  const audienceType = audienceTypeSetting?.stringValue;
  const minSizeString = minSizeSetting?.stringValue;
  const memberCount = getAudienceMemberCount(audience, audienceType);
  const parsedMinSize = minSizeString ? parseInt(minSizeString) : undefined;
  const minSize = parsedMinSize !== undefined && !isNaN(parsedMinSize) ? parsedMinSize : undefined;

  return { audienceType, minSizeString, minSize, memberCount };
};

export const isActivationAllowed = (audience: AudienceType, provider: IntegrationProviderType) => {
  const { audienceType, minSize, memberCount } = getActivationRequirements(audience, provider);
  if (!audienceType || minSize === undefined) {
    return false;
  }
  return (memberCount ?? 0) >= minSize;
};

/**
 * Email-based activation providers require a CRM connection to create a custom audience within
 * their platform. Check for that here.
 */
export const getActivationAudienceType = (
  provider: IntegrationProviderType,
): 'email' | 'domain' | 'ip' | undefined => {
  const activationCapability = provider?.capabilities?.find(
    (capability) => capability.id === 'audienceActivation',
  );
  const audienceTypeSetting = activationCapability?.settings?.find(
    (setting) => setting.name === 'audienceType',
  );
  const audienceType = audienceTypeSetting?.stringValue as 'email' | 'domain' | 'ip' | undefined;
  return audienceType;
};

export const getAudienceActivationConsent = (provider: IntegrationProviderType) => {
  if (provider.id === INTEGRATION_PROVIDER_ID.GOOGLE_ADS) {
    return (
      <Trans>
        By continuing, you consent to share email addresses associated with the companies included
        in your selected audience with the integrated vendor. Your information will be exchanged
        solely for the purpose of enhancing the platform's services and experience through audience
        activation. If consent is missing for EEA users, then the consent value is determined as not
        consented. Data from unconsented EEA users won't be processed and cannot be used for ad
        personalization using Customer Match. See FAQs and updates to consent mode for more details.
      </Trans>
    );
  }

  const audienceType = getActivationAudienceType(provider);
  switch (audienceType) {
    case 'email':
      return (
        <Trans>
          By continuing, you consent to share email addresses associated with the companies included
          in your selected audience with the integrated vendor. Your information will be exchanged
          solely for the purpose of enhancing the platform's services and experience through
          audience activation.
        </Trans>
      );
    case 'domain':
      return (
        <Trans>
          By continuing, you consent to share company domains with the integrated platform for the
          companies included in your selected audience. Your information will be exchanged solely
          for the purpose of enhancing the platform's services and experience through audience
          activation.
        </Trans>
      );
    case 'ip':
      return (
        <Trans>
          By continuing, you consent to share IPs associated with the companies included in your
          selected audience with the integrated vendor. Your information will be exchanged solely
          for the purpose of enhancing the platform's services and experience through audience
          activation.
        </Trans>
      );
  }

  throw new Error(
    `Unknown audience type: "${audienceType}" for provider: "${provider.name}" (${provider.id})`,
  );
};
